import React, { useEffect, useMemo, useState } from "react";
import {
  Card, Form, Select, InputNumber, Input, Button, message, Tag, Space, Radio,
  Checkbox, Modal, Segmented, Statistic, Row, Col,
} from "antd";
import { CarOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { scFreightList, scFreightCreate, scFreightUpdate, scFreightDelete, scFreightBulkUpdate, scGetEvents } from "../../api/client";
import { JalaliDateInput, todayISO, toJalali } from "../../utils/dateUtils";
import { byStr, byNum, byDate } from "../../utils/sorters";
import { useAuth } from "../../context/AuthContext";
import { fmtKg, fmtRial, statFmtRial } from "./scConstants";
import { PERIOD_PRESETS, periodRange } from "./scPeriods";
import DismissibleHelp from "../../components/DismissibleHelp";
import EditableTable from "../../components/EditableTable";
import RowActions from "../../components/RowActions";

const { Option } = Select;
const numFmt = (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const BEARER_META = {
  ours: { l: "با ما", c: "green" },
  supplier: { l: "با تأمین‌کننده", c: "blue" },
  other: { l: "سایر", c: "default" },
};

export default function FreightPage() {
  const { can } = useAuth();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);   // ردیفِ در حالِ ویرایش (Modal)
  const [editForm] = Form.useForm();
  const [preset, setPreset] = useState("all");
  const [q, setQ] = useState("");

  const range = useMemo(() => periodRange(preset), [preset]);

  const load = () => {
    const params = {};
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    if (q.trim()) params.q = q.trim();
    scFreightList(params)
      .then((r) => { setRows(r.data.rows || []); setSummary(r.data.summary || null); })
      .catch(() => message.error("خطا در بارگذاریِ لیستِ حمل"));
  };
  useEffect(load, [range.date_from, range.date_to, q]);   // eslint-disable-line
  useEffect(() => {
    scGetEvents({}).then((r) => setEvents((r.data || []).slice(0, 300))).catch(() => {});
  }, []);

  const submit = async () => {
    const v = await form.validateFields();
    setLoading(true);
    try {
      await scFreightCreate({ ...v, event_date: v.event_date || todayISO() });
      message.success("حمل ثبت شد");
      form.resetFields();
      form.setFieldsValue({ event_date: todayISO(), cost_bearer: "ours", cost_included_in_price: false });
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ثبت"); }
    setLoading(false);
  };

  const openEdit = (r) => {
    setEditRow(r);
    editForm.setFieldsValue({ ...r });
  };
  const saveEdit = async () => {
    const v = await editForm.validateFields();
    try {
      await scFreightUpdate(editRow.id, v);
      message.success("ویرایش شد");
      setEditRow(null);
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ویرایش"); }
  };
  const remove = async (id) => {
    try { await scFreightDelete(id); message.success("حذف شد"); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در حذف"); }
  };

  const canCreate = can("sc.event.create");
  const columns = [
    { title: "تاریخ", dataIndex: "event_date", width: 105, sorter: byDate("event_date"),
      defaultSortOrder: "descend", editType: "date", gxFilter: "dateRange", render: (v) => toJalali(v) },
    { title: "مبدأ", dataIndex: "origin", sorter: byStr("origin"), editType: "text", gxFilter: "enum", render: (v) => v || "—" },
    { title: "مقصد", dataIndex: "destination", sorter: byStr("destination"), editType: "text", gxFilter: "enum", render: (v) => v || "—" },
    { title: "بارنامه", dataIndex: "waybill_number", sorter: byStr("waybill_number"), editType: "text", render: (v) => v || "—" },
    { title: "راننده", dataIndex: "driver_name", sorter: byStr("driver_name"), editType: "text",
      render: (v, r) => v ? <span>{v}{r.driver_phone && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{r.driver_phone}</div>}</span> : "—" },
    { title: "وزن", dataIndex: "weight_kg", align: "center", sorter: byNum("weight_kg"), editType: "number",
      gxFilter: "num",
      editRule: (v) => (v !== null && v !== undefined && Number(v) < 0 ? "وزن نمی‌تواند منفی باشد" : null),
      render: (v) => v == null ? "—" : fmtKg(v) },
    { title: "کرایه", dataIndex: "cost_amount", align: "center", sorter: byNum("cost_amount"), editType: "number",
      gxFilter: "num",
      editRule: (v) => (v !== null && v !== undefined && Number(v) < 0 ? "کرایه نمی‌تواند منفی باشد" : null),
      render: (v) => <b>{fmtRial(v)}</b> },
    { title: "هزینه با", dataIndex: "cost_bearer", align: "center", sorter: byStr("cost_bearer"),
      gxFilter: "enum",
      gxFilterOptions: Object.entries(BEARER_META).map(([v, m]) => ({ text: m.l, value: v })),
      editType: "select",
      options: [{ value: "ours", label: "با ما" }, { value: "supplier", label: "با تأمین‌کننده" }, { value: "other", label: "سایر" }],
      render: (v, r) => (
        <span>
          <Tag color={(BEARER_META[v] || {}).c}>{(BEARER_META[v] || {}).l || v}</Tag>
          {r.cost_included_in_price && <Tag color="orange">در فی لحاظ شده</Tag>}
        </span>
      ) },
    { title: "رویداد", dataIndex: "movement_event_code", render: (v) => v || "—" },
    ...(canCreate ? [{
      title: "", key: "actions", width: 90, fixed: "right", align: "center", gxFilter: false,
      render: (_, r) => (
        <RowActions actions={[
          { key: "edit", label: "ویرایش", icon: <EditOutlined />, onClick: () => openEdit(r) },
          { key: "delete", label: "حذف", icon: <DeleteOutlined />, danger: true,
            confirm: "حذف شود؟", onClick: () => remove(r.id) },
        ]} />
      ),
    }] : []),
  ];

  const freightSummary = [
    { dataIndex: "weight_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "cost_amount", type: "sum", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "cost_bearer", type: "unique", labelOf: (v) => (BEARER_META[v] || {}).l || v },
    { dataIndex: "origin", type: "unique" },
    { dataIndex: "destination", type: "unique" },
  ];

  const formFields = (f) => (
    <>
      <Form.Item name="event_date" label="تاریخ حمل" rules={[{ required: true, message: "تاریخ لازم است" }]}>
        <JalaliDateInput />
      </Form.Item>
      <Space wrap>
        <Form.Item name="origin" label="مبدأ"><Input style={{ width: 150 }} placeholder="مثلاً سمنان (درین)" /></Form.Item>
        <Form.Item name="destination" label="مقصد"><Input style={{ width: 150 }} placeholder="مثلاً بافندگی نوربخش" /></Form.Item>
      </Space>
      <Space wrap>
        <Form.Item name="waybill_number" label="شماره بارنامه"><Input style={{ width: 140 }} /></Form.Item>
        <Form.Item name="weight_kg" label="وزن بار (کیلو)"><InputNumber min={0} style={{ width: 130 }} /></Form.Item>
      </Space>
      <Space wrap>
        <Form.Item name="driver_name" label="نام راننده"><Input style={{ width: 150 }} /></Form.Item>
        <Form.Item name="driver_phone" label="موبایل راننده"><Input style={{ width: 140 }} dir="ltr" /></Form.Item>
      </Space>
      <Form.Item name="cost_amount" label="کرایه (ریال)">
        <InputNumber min={0} style={{ width: "100%" }} formatter={numFmt} />
      </Form.Item>
      <Form.Item name="cost_bearer" label="هزینه با" initialValue="ours">
        <Radio.Group>
          <Radio.Button value="ours">ما</Radio.Button>
          <Radio.Button value="supplier">تأمین‌کننده</Radio.Button>
          <Radio.Button value="other">سایر</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item name="cost_included_in_price" valuePropName="checked" initialValue={false}
        tooltip="اگر تیک بخورد، این کرایه در فیِ فاکتورِ فروشنده کشیده شده و در سرجمعِ گزارشِ فی تمام‌شده دوباره حساب نمی‌شود.">
        <Checkbox>در فیِ تأمین‌کننده لحاظ شده</Checkbox>
      </Form.Item>
      <Form.Item name="movement_event_id" label="لینک به رویداد (اختیاری)">
        <Select allowClear showSearch optionFilterProp="children" placeholder="کد رویداد…">
          {events.map((e) => <Option key={e.id} value={e.id}>{e.event_code} — {e.item_name || e.fabric_type || e.event_type}</Option>)}
        </Select>
      </Form.Item>
      <Form.Item name="notes" label="توضیحات"><Input.TextArea rows={2} /></Form.Item>
    </>
  );

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>حمل و بارنامه</h2>
      <DismissibleHelp id="sc-freight"
        message="کرایه‌های حمل این‌جا جدا ثبت می‌شوند و در فیِ هر پارت نمی‌آیند؛ فقط ردیف‌های «هزینه با ما» که در فیِ فروشنده لحاظ نشده باشند، به‌صورتِ انتخابی در سرجمعِ گزارشِ «فی تمام‌شده» جمع می‌شوند." />

      {summary && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}><Card size="small"><Statistic title="تعداد حمل" value={summary.count} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="جمع کل کرایه‌ها" value={summary.total_cost ?? 0} formatter={statFmtRial} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="سهم ما (مؤثر در فی)" value={summary.our_cost ?? 0} formatter={statFmtRial}
            valueStyle={{ color: "#d46b08" }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="لحاظ‌شده در فی فروشنده" value={summary.included_count} /></Card></Col>
        </Row>
      )}

      {/* چیدمانِ گرید (نه Space) تا با جدولِ max-content حلقهٔ رشدِ بی‌نهایت نسازد و در لپ‌تاپ جا شود */}
      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={9} xl={8}>
          <Card title="ثبت حمل جدید">
            {canCreate ? (
              <Form form={form} layout="vertical" style={{ direction: "rtl" }}
                initialValues={{ event_date: todayISO(), cost_bearer: "ours", cost_included_in_price: false }}>
                {formFields(form)}
                <Button type="primary" icon={<CarOutlined />} loading={loading} onClick={submit} block>
                  ثبت حمل
                </Button>
              </Form>
            ) : <div style={{ color: "#888" }}>برای ثبت حمل دسترسی ندارید.</div>}
          </Card>
        </Col>

        {/* minWidth:0 روی Col تا کشویِ افقیِ جدول داخلِ ستون بماند و ستون را بی‌نهایت پهن/بلند نکند */}
        <Col xs={24} lg={15} xl={16} style={{ minWidth: 0 }}>
          <Card title="لیست حمل‌ها" styles={{ body: { overflowX: "auto" } }}
            extra={
              <Space wrap>
                <Segmented size="small" value={preset} onChange={setPreset}
                  options={PERIOD_PRESETS.map((p) => ({ label: p.label, value: p.key }))} />
                <Input.Search allowClear placeholder="مبدأ/مقصد/راننده/بارنامه…" style={{ width: 210 }}
                  onSearch={setQ} onChange={(e) => !e.target.value && setQ("")} />
              </Space>
            }>
            <EditableTable layoutId="sc-freight" dataSource={rows} columns={columns} rowKey="id"
              pagination={{ pageSize: 12 }}
              gxSummary={freightSummary}
              canEditRow={() => (canCreate ? true : "دسترسیِ ویرایش ندارید")}
              onSaveMany={async (items) => { await scFreightBulkUpdate(items); load(); }} />
          </Card>
        </Col>
      </Row>

      <Modal open={!!editRow} title={`ویرایش حمل #${editRow?.id || ""}`} onCancel={() => setEditRow(null)}
        onOk={saveEdit} okText="ذخیره" cancelText="انصراف" destroyOnClose>
        <Form form={editForm} layout="vertical" style={{ direction: "rtl" }}>
          {formFields(editForm)}
        </Form>
      </Modal>
    </div>
  );
}
